import { useLocales } from '@/Locales';
import {
  httpImageProcessBatchDelete,
  httpImageProcessClassify,
  httpImageProcessDeleteTask,
  httpImageProcessQueryPatientTask,
  httpImageProcessQueryTaskDetails,
  httpImageProcessSaveTask,
  httpImageProcessUpdateClassify,
  httpImageRecognitionCreate,
  httpImageRecognitionFind,
  httpVidepProcessSaveTask,
} from '@/services/imageProcess';
import { CompressFilesProps } from '@/utils/picControler/ImageBase';
import { decrypt } from '@/utils/sm-crypto';
import { message } from '@sinohealth/butterfly-ui-components';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
// @ts-ignore

class TaskQueue {
  private queue: (() => Promise<void>)[] = [];

  private processing: boolean = false;

  constructor() {
    this.removeAt = this.removeAt.bind(this);
  }

  enqueue(task: () => Promise<void>) {
    this.queue.push(task);
  }

  removeAt(index: number): void {
    console.log('Current queue:', this.queue);
    if (index < 0 || index >= this.queue.length) {
      console.log('Index out of bounds');
      return;
    }
    this.queue.splice(index, 1); // 移除指定索引的任务
    console.log('Queue after removal:', this.queue);
  }

  async processNext(): Promise<void> {
    if (this.processing) return;
    this.processing = true;
    console.log('Processing next task, queue length:', this.queue.length);
    if (this.queue.length === 0) {
      this.processing = false;
      return;
    }

    const currentTask = this.queue.shift(); // 从队列中移除并返回第一个任务
    if (currentTask) {
      try {
        await currentTask(); // 执行当前任务
      } catch (error) {
        console.error('Error executing task:', error);
      }
      await this.processNext(); // 递归地处理下一个任务
    }

    this.processing = false;
  }

  async processAll(): Promise<void> {
    console.log('Starting to process all tasks, initial queue length:', this.queue.length);
    while (this.queue.length > 0) {
      // eslint-disable-next-line no-await-in-loop
      await this.processNext();
      console.log('Processed a task, remaining queue length:', this.queue.length);
    }
  }
}

/**
 * @description: 处理图片任务
 * @author: KaifengLi
 * @version: v2.27.0
 * @Date: 2024-11-19 14:59:28
 */
export const useProcessFile = (scene = 'recognition', fileType = '') => {
  const [params] = useSearchParams();
  const patientId: string = params.get('patientId') || ''; // 智能识别的患者id
  const id = params.get('id') || ''; // 患者详情的患者id
  const { t } = useLocales();

  // const taskQueue = new TaskQueue();
  const [taskIndex, setTaskIndex] = useState(0);
  const [taskProcessing, setTaskProcessing] = useState(false);
  const [processDetail, setProcessDetail] = useState<any>();
  const [processSucc, setProcessSucc] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<CompressFilesProps[]>([]);

  const isMounted = useRef(true);
  let intervalId: any = null;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 定义处理单个文件的异步函数
  const processFile = async (file: CompressFilesProps) => {
    try {
      setTaskProcessing(true);
      console.log(`Processing image: ${file?.file?.name}`);
      const formDate = new FormData();
      formDate.append('file', file?.file);
      formDate.append('patientId', patientId || id);
      formDate.append('taskType', scene);
      if (fileType) {
        formDate.append('fileType', fileType);
      }
      setProcessDetail(undefined);
      const task: any = await httpImageProcessSaveTask(formDate);
      // 状态(in_processing-处理中,completed-已完成，fail-失败，unqualified-不合格)

      await httpImageProcessQueryTaskDetailsReq(task?.id);
      // if (task?.id && task?.status === 'in_processing') {
      //   await httpImageProcessQueryTaskDetailsReq(task.id);
      // } else if (scene === 'report') {
      //   await httpImageProcessQueryTaskDetailsReq(task.id);
      // } else {
      //   setProcessSucc(true);
      // }
      if (task?.isRepeat) {
        message.success(`文件【${file.file.name}】已上传`);
      }
      console.log(`Finished processing image: ${file?.file?.name}`);
      setProcessSucc(false);
      setTaskProcessing(false);
      if (isMounted.current) {
        setUploadFiles((prev) => prev.filter((item) => item?.file?.name !== file?.file?.name));
      }
    } catch (error) {
      console.log('Error processing file:', error);
      if (isMounted.current) {
        setTaskProcessing(false);
        intervalId && clearInterval(intervalId);
        removeFileFromQueue(0);
      }
    }
  };

  /** 查询图片处理任务详情 */
  const httpImageProcessQueryTaskDetailsReq = (taskId: string) => {
    return new Promise((resolve, reject) => {
      intervalId = setInterval(async () => {
        try {
          const res: any = await httpImageProcessQueryTaskDetails({ taskId });
          // console.log(isMounted.current);
          const { status } = res;
          if (status !== 'unqualified') {
            setProcessDetail(res);
          }
          // 不是处理中的时候，清除定时器
          if (status !== 'in_processing') {
            clearInterval(intervalId); // 停止轮询
            // 有id时候代表是在患者详情当个上传，需要返回数据
            setProcessSucc(true);
            if (status === 'completed') {
              message.success('图片处理成功');
            }
            if (status === 'unqualified') {
              message.error('图片不清晰，请重新上传');
            }
            if (status === 'fail') {
              message.error(res?.failReason || '图片处理失败');
            }
            resolve(true); // 处理状态不等于 in_processing 时，执行下一步
          }
        } catch (error) {
          clearInterval(intervalId); // 发生错误时停止轮询
          if (isMounted.current) {
            reject(error); // 抛出错误
          }
        }
      }, 3000);
    });
  };

  // 添加文件到队列
  const addFileToQueue = (files: CompressFilesProps[]) => {
    // files.forEach((image) => {
    //   taskQueue.enqueue(() => processFile(image));
    // });
    setTaskIndex(0);
    setUploadFiles((prev) => [...prev, ...files]);
    // taskQueue.processAll(); // 开始处理所有任务
  };

  // 根据索引移除队列成员
  const removeFileFromQueue = (index: number) => {
    // taskQueue.removeAt(index);
    const file = [...uploadFiles];
    file.splice(index, 1); // 移除指定索引的任务
    if (isMounted.current) {
      setUploadFiles(file);
    }
  };

  useEffect(() => {
    // console.log('uploadFiles', uploadFiles);
    if (taskProcessing) return;
    if (uploadFiles.length > 0) {
      processFile(uploadFiles[taskIndex]);
    }
  }, [uploadFiles, taskIndex]);

  return {
    uploadFiles,
    setUploadFiles,
    addFileToQueue,
    removeFileFromQueue,
    processDetail,
    processSucc,
    setProcessDetail,
  };
};

/**
 * @description: 查询图片分类列表
 * @author: KaifengLi
 * @version: v2.27.0
 * @Date: 2024-11-20 13:39:59
 */
export const useProcessClassify = () => {
  const [params] = useSearchParams();
  const patientId: string = params.get('patientId') || '';

  const [reloadClassify, setReloadClassify] = useState(false);
  // 加载成功，用于控制绑定ocr任务
  const [loadClassifySucc, setLoadClassifySucc] = useState(false);
  const [unqualiFied, setUnqualiFied] = useState<any[]>([]);
  const [classifyColumns, setClassifyColumns] = useState<any>([]);
  const [completedFileUrls, setCompletedFileUrls] = useState<string[]>([]);

  const httpImageProcessClassifyReq = async () => {
    try {
      const res: any = await httpImageProcessClassify({ patientId });
      // console.log(res);
      setReloadClassify(false);
      const {
        allCompletedFileUrlList,
        dischargeRecordList,
        drugRecordList,
        geneticTestingList,
        imageReportList,
        inspectionReportList,
        medicalRecordList,
        otherInformationList,
        pathologyReportList,
        surgicalRecordList,
        unqualifiedList,
      } = res;
      setUnqualiFied(unqualifiedList || []);
      setCompletedFileUrls(allCompletedFileUrlList || []);
      setClassifyColumns([
        {
          label: '入院记录',
          value: 'medicalRecord',
          items: medicalRecordList,
        },
        {
          label: '出院记录',
          value: 'hospitalDischargeRecord',
          items: dischargeRecordList,
        },
        {
          label: '影像报告',
          value: 'imageReport',
          items: imageReportList,
        },
        {
          label: '手术信息',
          value: 'surgicalRecord',
          items: surgicalRecordList,
        },
        {
          label: '病理报告',
          value: 'pathologyReport',
          items: pathologyReportList,
        },
        {
          label: '检验报告',
          value: 'inspectionReport',
          items: inspectionReportList,
        },
        {
          label: '基因检测',
          value: 'geneticTesting',
          items: geneticTestingList,
        },
        {
          label: '用药记录',
          value: 'drugRecord',
          items: drugRecordList,
        },
        {
          label: '其他',
          value: 'otherInformation',
          items: otherInformationList,
        },
      ]);
      setLoadClassifySucc(true);
    } catch (err) {
      console.log(err);
    } finally {
      setReloadClassify(false);
      setLoadClassifySucc(false);
    }
  };

  useEffect(() => {
    patientId && httpImageProcessClassifyReq();
  }, [patientId]);

  useEffect(() => {
    // console.log({ reloadClassify });
    reloadClassify && httpImageProcessClassifyReq();
  }, [reloadClassify]);

  return {
    unqualiFied,
    classifyColumns,
    loadClassifySucc,
    setLoadClassifySucc,
    setClassifyColumns,
    setReloadClassify,
    completedFileUrls,
    setCompletedFileUrls,
  };
};

export const classifyData = [
  {
    label: '入院记录',
    value: 'medicalRecord',
  },
  {
    label: '出院记录',
    value: 'hospitalDischargeRecord',
  },
  {
    label: '影像报告',
    value: 'imageReport',
  },
  {
    label: '手术信息',
    value: 'surgicalRecord',
  },
  {
    label: '病理报告',
    value: 'pathologyReport',
  },
  {
    label: '检验报告',
    value: 'inspectionReport',
  },
  {
    label: '基因检测',
    value: 'geneticTesting',
  },
  {
    label: '用药记录',
    value: 'drugRecord',
  },
  {
    label: '其他',
    value: 'otherInformation',
  },
];

/**
 * @description: 更新图片分类
 * @author: KaifengLi
 * @version: v2.27.0
 * @Date: 2024-11-20 14:12:27
 */
export const useUpdateClassify = () => {
  const [updateParams, setUpdateParams] = useState<any>();
  const [uploadSucc, setUploadSucc] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);

  const httpImageProcessUpdateClassifyReq = async () => {
    try {
      setLoadingUpload(true);
      const res = await httpImageProcessUpdateClassify(updateParams);
      // console.log(res);
      setUploadSucc(true);
    } catch (error) {
      console.log(error);
    } finally {
      setUploadSucc(false);
      setLoadingUpload(false);
    }
  };

  useEffect(() => {
    updateParams && httpImageProcessUpdateClassifyReq();
  }, [updateParams]);

  return { setUpdateParams, uploadSucc, setLoadingUpload, loadingUpload };
};

/**
 * @description: 删除图片处理任务
 * @author: KaifengLi
 * @version: v2.27.0
 * @Date: 2024-11-20 14:14:37
 */
export const useDeleteTask = () => {
  const [deleteParams, setDeleteParams] = useState<any>();
  const [deleteSucc, setDeleteSucc] = useState(false);

  const httpImageProcessDeleteTaskReq = async () => {
    try {
      const res = await httpImageProcessDeleteTask(deleteParams);
      // console.log(res);
      setDeleteSucc(true);
    } catch (error) {
      console.log(error);
    } finally {
      setDeleteSucc(false);
      setDeleteParams(undefined);
    }
  };

  useEffect(() => {
    deleteParams && httpImageProcessDeleteTaskReq();
  }, [deleteParams]);

  return { setDeleteParams, deleteSucc };
};

/**
 * @description: 批量删除识别图片
 * @author: KaifengLi
 * @version: v2.27.0
 * @Date: 2024-11-21 14:06:22
 */
export const useBatchDeleteTask = () => {
  const [batchParams, setBatchParams] = useState<any>();
  const [batchDeleteSucc, setBatchDeleteSucc] = useState(false);

  const httpImageProcessBatchDeleteReq = async () => {
    try {
      const res = await httpImageProcessBatchDelete(batchParams);
      // console.log(res);
      setBatchDeleteSucc(true);
    } catch (error) {
      console.log(error);
    } finally {
      setBatchDeleteSucc(false);
      setBatchParams(undefined);
    }
  };

  useEffect(() => {
    batchParams && httpImageProcessBatchDeleteReq();
  }, [batchParams]);

  return { setBatchParams, batchDeleteSucc };
};

/**
 * @description: 批量处理文件
 * @author: KaifengLi
 * @param {*} scene 场景 单模块:single,多模块:multi
 * @param {*} fileType 单模块-模块类型, 入院记录-medicalRecord,出院记录-hospitalDischargeRecord
 * @version: v2.27.0
 * @Date: 2024-11-27 16:16:23
 */
export const useQueryPatientTask = (param?: any) => {
  const { scene = 'multi', fileType = '', recordId, callback } = param;
  const [params] = useSearchParams();
  const patientId = decrypt(params.get('patientId') || '');
  const id = decrypt(params.get('id') || ''); // 患者详情的患者id

  const [recognitTaskId, setRecognitTaskId] = useState(''); // 图片智能识别编号
  const [saveLoading, setSaveLoading] = useState(false);
  const [patientTasks, setPatientTasks] = useState<any[]>([]);
  const [uploadFiles, setUploadFiles] = useState<CompressFilesProps[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<CompressFilesProps[]>([]); // 上传中的文件
  const [videoUploadFiles, setVideoUploadFiles] = useState<CompressFilesProps[]>([]);

  // 使用 useRef 来存储定时器和任务 ID 列表
  const intervalRef = useRef<any>(null);
  const taskIdsRef = useRef<string[]>([]);

  const handleSetUploadingFiles = (file: CompressFilesProps, remove: boolean = false) => {
    if (remove) {
      setUploadingFiles((prev) => prev.filter((el) => el.id !== file.id));
    } else {
      setUploadingFiles((prev) => [...prev, file]);
    }
  };

  /** 保存图片处理任务 */
  const httpImageProcessSaveTaskReq = async (file: CompressFilesProps, index: number) => {
    try {
      handleSetUploadingFiles(file);
      const formDate = new FormData();
      formDate.append('file', file?.file);
      formDate.append('recognitionTaskId', recognitTaskId);
      const res: any = await httpImageProcessSaveTask(formDate);
      // console.log({ id: res.id });
      taskIdsRef.current.push(res.id);
      handleSetUploadingFiles(file, true);
      if (res?.status === 'completed') {
        if (res?.isRepeat) {
          !fileType && message.success(`文件【${file.file.name}】已上传`);
        }
        callback(false);
      } else {
        httpImageProcessQueryPatientTaskReq();
        interFn();
      }
    } catch (err) {
      console.log(err);
      handleSetUploadingFiles(file, true);
      if (callback && fileType) {
        callback(false);
      }
    }
  };

  /** 保存视频处理任务 */
  const httpVidepProcessSaveTaskReq = async (file: CompressFilesProps, index: number) => {
    try {
      handleSetUploadingFiles(file);
      const formDate = new FormData();
      formDate.append('file', file.file);
      formDate.append('recognitionTaskId', recognitTaskId);
      const res: any = await httpVidepProcessSaveTask(formDate);
      // console.log({ id: res.id });
      handleSetUploadingFiles(file, true);
      taskIdsRef.current.push(res.id);
      if (res?.status === 'completed') {
        if (res?.isRepeat) {
          !fileType && message.success(`视频【${file.file.name}】已上传`);
        }
        callback(false);
      } else {
        httpImageProcessQueryPatientTaskReq();
        interFn();
      }
    } catch (err) {
      console.log(err);
      handleSetUploadingFiles(file, true);
      if (callback && fileType) {
        callback(false);
      }
    }
  };

  /** 查询图片处理任务详情 */
  const httpImageProcessQueryTaskDetailsReq = async (taskId: string) => {
    try {
      const res: any = await httpImageProcessQueryTaskDetails({ taskId });
      const { status } = res;
      if (status !== 'in_processing') {
        if (status === 'completed') {
          message.success('图片处理成功');
        }
        if (status === 'unqualified') {
          message.error('图片不清晰，请重新上传');
        }
        if (status === 'fail') {
          message.error(res?.failReason || '图片处理失败');
        }
        if (fileType && callback) {
          callback(res);
          httpImageProcessQueryPatientTaskReq();
        }
      }
      if (callback && !fileType) {
        callback();
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** 轮询图片任务列表 */
  const interFn = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    intervalRef.current = setInterval(async () => {
      await httpImageProcessQueryPatientTaskReq();
    }, 2000);
  };

  /** 查询患者图片处理任务编号 */
  const httpImageProcessQueryPatientTaskReq = async () => {
    try {
      const res: any = await httpImageProcessQueryPatientTask({
        patientId: patientId || id,
        taskType: scene,
        statusList: ['in_processing'],
        fileType,
      });
      const { taskList = [] } = res || {};
      // console.log({ res, taskIds: taskIdsRef.current });

      if (taskIdsRef.current.length > 0 && taskList.length !== taskIdsRef.current.length) {
        const ids = taskList?.map((item: any) => item.id) || [];
        const completedFiles = taskIdsRef.current.filter((el: any) => !ids.includes(el));

        if (!completedFiles.length) return;

        completedFiles.forEach((item: any) => {
          setTimeout(() => {
            httpImageProcessQueryTaskDetailsReq(item);
          }, 0);
        });

        taskIdsRef.current = completedFiles.length
          ? taskIdsRef.current.filter((el: any) => !completedFiles.includes(el))
          : [];
        // console.log({ taskIds: taskIdsRef.current });
      }

      if (taskList.length === 0) {
        clearInterval(intervalRef.current);
      } else {
        clearInterval(intervalRef.current);
        interFn();
        taskIdsRef.current = taskList.map((el: any) => el.id);
      }
      setPatientTasks(taskList);
    } catch (err) {
      console.log(err);
      clearInterval(intervalRef.current);
    }
  };

  /** 获取患者当前已有的智能识别任务 */
  const httpImageRecognitionFindReq = async () => {
    try {
      const res: any = await httpImageRecognitionFind({
        patientId: patientId || id,
        taskType: scene,
        moduleType: fileType || undefined,
        recordId,
      });
      console.log('获取患者当前已有的智能识别任务', res);
      if (!res) {
        httpImageRecognitionCreateReq();
      } else {
        setRecognitTaskId(res?.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** 创建图片智能识别任务 */
  const httpImageRecognitionCreateReq = async () => {
    try {
      const res: any = await httpImageRecognitionCreate({
        patientId: patientId || id,
        taskType: scene,
        moduleType: fileType || undefined,
        recordId,
      });
      console.log('创建图片智能识别任务', res);
      setRecognitTaskId(res);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (uploadFiles.length) {
      if (!recognitTaskId) {
        httpImageRecognitionFindReq();
        return;
      }
      // clearInterval(intervalRef.current);
      // intervalRef.current = null;
      setSaveLoading(true);
      const allFiles = [...uploadFiles];
      console.log('开始上传之前', { allFiles, uploadFiles });
      Promise.all(
        allFiles.map((file, index) => {
          if (file.new) {
            // 图片上传
            if (file.file.type.includes('image')) {
              return httpImageProcessSaveTaskReq(file, index);
            }
            // 视频上传
            return httpVidepProcessSaveTaskReq(file, index);
          }
          return Promise.resolve();
        }),
      )
        .then(() => {
          console.log('all');
          if (!intervalRef.current) httpImageProcessQueryPatientTaskReq();
        })
        .catch((error) => {
          console.error('Error processing files:', error);
        })
        .finally(() => {
          setUploadFiles([]);
          setVideoUploadFiles([]);
          setSaveLoading(false);
          allFiles.forEach((el) => {
            if (el.new) {
              handleSetUploadingFiles(el, true);
            }
          });
          console.log('finally');
        });
    }
  }, [uploadFiles, recognitTaskId]);

  useEffect(() => {
    if (!fileType) {
      httpImageProcessQueryPatientTaskReq();
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [fileType]);

  return {
    uploadFiles,
    uploadingFiles,
    setUploadFiles,
    patientTasks,
    saveLoading,
    recognitTaskId,
    setVideoUploadFiles,
  };
};

interface ProcessingFilesProps extends CompressFilesProps {
  uploadStatus?: 'waiting' | 'uploading';
}

export const UploadVideoStatusTip: any = {
  waiting: '等待上传中...',
  uploading: '视频上传中...',
  processing: '视频检测中...',
};

/**
 * @description: 队列处理文件
 * @author: KaifengLi
 * @param {*} param
 * @version: v2.27.0
 * @Date: 2025-07-15 15:00:00
 */
export const useQueuePatientTask = (param?: any) => {
  const { scene = 'multi', fileType = '', recordId, callback } = param;
  const [params] = useSearchParams();
  const patientId: string = params.get('patientId') || '';
  const id = params.get('id') || ''; // 患者详情的患者id

  const [recognitTaskId, setRecognitTaskId] = useState(''); // 图片智能识别编号
  const [saveLoading, setSaveLoading] = useState(false);
  const [patientTasks, setPatientTasks] = useState<any[]>([]);
  const [uploadFiles, setUploadFiles] = useState<CompressFilesProps[]>([]);
  const [processingFiles, setProcessingFiles] = useState<ProcessingFilesProps[]>([]); // 处理中的文件（包括等待上传和正在上传）

  // 使用 useRef 来存储定时器和任务 ID 列表
  const intervalRef = useRef<any>(null);
  const taskIdsRef = useRef<string[]>([]);

  /** 统一管理处理中的文件状态 */
  const handleSetProcessingFiles = (
    file: CompressFilesProps & { uploadStatus?: 'waiting' | 'uploading' },
    remove: boolean = false,
  ) => {
    if (remove) {
      setProcessingFiles((prev) => prev.filter((el) => el.id !== file.id));
      return;
    }

    setProcessingFiles((prev) => {
      const existingIndex = prev.findIndex((el) => el.id === file.id);
      if (existingIndex >= 0) {
        // 更新现有文件的状态
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...file };
        return updated;
      }
      // 添加新文件
      return [...prev, file];
    });
  };

  /** 将文件状态从等待改为上传中 */
  const moveFileToUploading = (file: CompressFilesProps) => {
    handleSetProcessingFiles({ ...file, uploadStatus: 'uploading' });
  };

  /** 保存图片处理任务 */
  const httpImageProcessSaveTaskReq = async (file: CompressFilesProps, index: number) => {
    try {
      moveFileToUploading(file); // 移动文件状态：从未上传到上传中
      const formDate = new FormData();
      formDate.append('file', file?.file);
      formDate.append('recognitionTaskId', recognitTaskId);
      const res: any = await httpImageProcessSaveTask(formDate);
      // console.log({ id: res.id });
      taskIdsRef.current.push(res.id);
      handleSetProcessingFiles(file, true);
      if (res?.status === 'completed') {
        if (res?.isRepeat) {
          !fileType && message.success(`文件【${file.file.name}】已上传`);
        }
        callback(false);
      } else {
        httpImageProcessQueryPatientTaskReq();
        interFn();
      }
    } catch (err) {
      console.log(err);
      handleSetProcessingFiles(file, true);
      if (callback && fileType) {
        callback(false);
      }
    }
  };

  /** 保存视频处理任务 */
  const httpVidepProcessSaveTaskReq = async (file: CompressFilesProps, index: number) => {
    try {
      moveFileToUploading(file); // 移动文件状态：从未上传到上传中
      const formDate = new FormData();
      formDate.append('file', file.file);
      formDate.append('recognitionTaskId', recognitTaskId);
      const res: any = await httpVidepProcessSaveTask(formDate);
      // console.log({ id: res.id });
      handleSetProcessingFiles(file, true);
      taskIdsRef.current.push(res.id);
      if (res?.status === 'completed') {
        if (res?.isRepeat) {
          !fileType && message.success(`视频【${file.file.name}】已上传`);
        }
        callback(false);
      } else {
        httpImageProcessQueryPatientTaskReq();
        interFn();
      }
    } catch (err) {
      console.log(err);
      handleSetProcessingFiles(file, true);
      if (callback && fileType) {
        callback(false);
      }
    }
  };

  /** 查询图片处理任务详情 */
  const httpImageProcessQueryTaskDetailsReq = async (taskId: string) => {
    try {
      const res: any = await httpImageProcessQueryTaskDetails({ taskId });
      const { status } = res;
      if (status !== 'in_processing') {
        if (status === 'completed') {
          message.success('处理成功');
        }
        if (status === 'unqualified') {
          message.error('不清晰，请重新上传');
        }
        if (status === 'fail') {
          message.error(res?.failReason || '处理失败');
        }
        if (fileType && callback) {
          callback(res);
          httpImageProcessQueryPatientTaskReq();
        }
      }
      if (callback && !fileType) {
        callback();
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** 轮询图片任务列表 */
  const interFn = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    intervalRef.current = setInterval(async () => {
      await httpImageProcessQueryPatientTaskReq();
    }, 2000);
  };

  /** 查询患者图片处理任务编号 */
  const httpImageProcessQueryPatientTaskReq = async () => {
    try {
      const res: any = await httpImageProcessQueryPatientTask({
        patientId: patientId || id,
        taskType: scene,
        statusList: ['in_processing'],
        fileType,
      });
      const { taskList = [] } = res || {};
      // console.log({ res, taskIds: taskIdsRef.current });

      if (taskIdsRef.current.length > 0 && taskList.length !== taskIdsRef.current.length) {
        const ids = taskList?.map((item: any) => item.id) || [];
        const completedFiles = taskIdsRef.current.filter((el: any) => !ids.includes(el));

        if (!completedFiles.length) return;

        completedFiles.forEach((item: any) => {
          setTimeout(() => {
            httpImageProcessQueryTaskDetailsReq(item);
          }, 0);
        });

        taskIdsRef.current = completedFiles.length
          ? taskIdsRef.current.filter((el: any) => !completedFiles.includes(el))
          : [];
        // console.log({ taskIds: taskIdsRef.current });
      }

      if (taskList.length === 0) {
        clearInterval(intervalRef.current);
      } else {
        clearInterval(intervalRef.current);
        interFn();
        taskIdsRef.current = taskList.map((el: any) => el.id);
      }
      const fileUnit = ['mp4', 'mov'];
      const filterTaskList = taskList?.filter((el: any) =>
        fileUnit.includes(el.beforeFilePath.split('.').pop()),
      );
      if (filterTaskList.length) {
        setPatientTasks(filterTaskList);
      } else {
        setPatientTasks([]);
        clearInterval(intervalRef.current);
      }
    } catch (err) {
      console.log(err);
      clearInterval(intervalRef.current);
    }
  };

  /** 获取患者当前已有的智能识别任务 */
  const httpImageRecognitionFindReq = async () => {
    try {
      const res: any = await httpImageRecognitionFind({
        patientId: patientId || id,
        taskType: scene,
        moduleType: fileType || undefined,
        recordId,
      });
      console.log('获取患者当前已有的智能识别任务', res);
      if (!res) {
        httpImageRecognitionCreateReq();
      } else {
        setRecognitTaskId(res?.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** 创建图片智能识别任务 */
  const httpImageRecognitionCreateReq = async () => {
    try {
      const res: any = await httpImageRecognitionCreate({
        patientId: patientId || id,
        taskType: scene,
        moduleType: fileType || undefined,
        recordId,
      });
      console.log('创建图片智能识别任务', res);
      setRecognitTaskId(res);
    } catch (error) {
      console.log(error);
    }
  };

  /** 分批处理文件上传，每批最多5个文件 */
  const processFilesInBatches = async (files: CompressFilesProps[]) => {
    const BATCH_SIZE = 5; // 每批最多同时上传5个文件
    const newFiles = files.filter((file) => file.new);

    // 初始化未上传文件列表
    setProcessingFiles(newFiles.map((file) => ({ ...file, uploadStatus: 'waiting' as const })));

    console.log('开始分批上传', { totalFiles: newFiles.length, batchSize: BATCH_SIZE });

    // 将文件分成批次
    const batches: CompressFilesProps[][] = [];
    for (let i = 0; i < newFiles.length; i += BATCH_SIZE) {
      batches.push(newFiles.slice(i, i + BATCH_SIZE));
    }

    // 使用 reduce 串行处理每个批次，只有当前批次全部成功才进行下一批
    await batches.reduce(async (previousPromise, batch, batchIndex) => {
      await previousPromise; // 等待前一个批次完成

      console.log(`处理第 ${batchIndex + 1} 批，共 ${batch.length} 个文件`);

      // 处理当前批次的所有文件（批次内并行）
      // 如果任何一个文件上传失败，整个批次失败，停止后续批次
      await Promise.all(
        batch.map((file, index) => {
          const globalIndex = batchIndex * BATCH_SIZE + index;
          // 图片上传
          if (file.file.type.includes('image')) {
            return httpImageProcessSaveTaskReq(file, globalIndex);
          }
          // 视频上传
          return httpVidepProcessSaveTaskReq(file, globalIndex);
        }),
      );

      console.log(`第 ${batchIndex + 1} 批上传全部成功，继续下一批`);
    }, Promise.resolve());
  };

  useEffect(() => {
    if (uploadFiles.length) {
      if (!recognitTaskId) {
        httpImageRecognitionFindReq();
        return;
      }

      setSaveLoading(true);
      const allFiles = [...uploadFiles];
      console.log('开始上传之前', { allFiles, uploadFiles });

      // 使用队列上传替代并发上传
      processFilesInBatches(allFiles)
        .then(() => {
          console.log('所有批次上传完成');
          if (!intervalRef.current) httpImageProcessQueryPatientTaskReq();
        })
        .catch((error) => {
          console.error('文件上传过程中发生错误:', error);
        })
        .finally(() => {
          setUploadFiles([]);
          setProcessingFiles([]); // 清空处理中的文件列表
          setSaveLoading(false);
          allFiles.forEach((el) => {
            if (el.new) {
              handleSetProcessingFiles(el, true);
            }
          });
          console.log('上传流程结束');
        });
    }
  }, [uploadFiles, recognitTaskId]);

  useEffect(() => {
    if (!fileType) {
      httpImageProcessQueryPatientTaskReq();
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [fileType]);

  return {
    uploadFiles,
    processingFiles, // 完整的处理中文件列表
    setProcessingFiles, // 统一的文件状态管理函数
    setUploadFiles,
    patientTasks,
    saveLoading,
    recognitTaskId,
  };
};

/** 最大上传文件数量 */
export const maxUploadCount = 50;
